/**
 * "Use part of the curve" (W2-05; DESIGN.md §4.12 slice, rpe-format.md F§6.6): RPE's easingLeft / easingRight. The
 * part [start, end] of the curve is stretched over the whole segment and renormalised, as Phira does.
 *
 * A bar with two thumbs (drag, or focus a thumb and use the arrow keys: 0.01 per step, Shift 0.1, Home / End) and
 * two number fields. Disabled with the reason for Linear (Phira ignores the part), Stay and Bezier curves and note
 * controls; parts that start at or after their end, or that start and end at the same height (Phira would divide by
 * zero and show nothing), are refused and never reported: the part stays as it was and the reason of the last
 * refused part is shown (an alert) until the next accepted change.
 */

import { useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { Ease } from '../../core/ease/index';
import type { Ease as EaseValue } from '../../core/ease/types';
import type { MessageRef } from '../../core/util/types';
import { Button, CurveThumb, NumberField } from '../../ui';
import { m } from './easing.messages';
import s from './Easing.module.css';
import { useText } from './hooks';

export interface SliceEditorProps {
  /** The numbered curve being cut (left / right are its part). */
  readonly ease: Extract<EaseValue, { kind: 'tween' }>;
  /** Why the part cannot be edited (null: it can). */
  readonly disabledReason: MessageRef | null;
  /** A valid part; `final` false while dragging. */
  onChange(e: Extract<EaseValue, { kind: 'tween' }>, final: boolean): void;
}

const STEP = 0.01;

function round2(v: number): number {
  const r = Math.round(v * 100) / 100;
  return r === 0 ? 0 : r;
}

/** Why a part [left, right] of curve `id` is refused, or null. */
export function sliceProblem(id: number, left: number, right: number): MessageRef | null {
  if (!(left >= 0 && right <= 1 && left < right)) return { key: m.sliceEmpty };
  if (!(left === 0 && right === 1) && Ease.sliceIsDegenerate(id, left, right)) return { key: m.sliceDegenerate };
  return null;
}

export function SliceEditor(props: SliceEditorProps) {
  const { t, tr } = useText();
  const { ease, disabledReason } = props;
  const disabled = disabledReason !== null;
  const barRef = useRef<HTMLDivElement>(null);
  const drag = useRef<'left' | 'right' | null>(null);
  /** Why the last proposed part was refused (cleared by the next accepted change). */
  const [refused, setRefused] = useState<MessageRef | null>(null);
  const problem = disabled ? null : (refused ?? sliceProblem(ease.id, ease.left, ease.right));

  const accept = (e: Extract<EaseValue, { kind: 'tween' }>, final: boolean): void => {
    setRefused(null);
    props.onChange(e, final);
  };

  const propose = (left: number, right: number, final: boolean): void => {
    const l = round2(Math.min(Math.max(left, 0), 1));
    const r = round2(Math.min(Math.max(right, 0), 1));
    const why = sliceProblem(ease.id, l, r);
    if (why !== null) {
      setRefused(why);
      return;
    }
    accept({ kind: 'tween', id: ease.id, left: l, right: r }, final);
  };

  const fromPointer = (e: ReactPointerEvent<HTMLElement>): number => {
    const rect = barRef.current?.getBoundingClientRect();
    if (rect === undefined || rect.width <= 0) return 0;
    return (e.clientX - rect.left) / rect.width;
  };

  const thumbKeys = (which: 'left' | 'right') => (e: KeyboardEvent<HTMLElement>) => {
    if (disabled) return;
    const big = e.shiftKey ? 10 : 1;
    const cur = which === 'left' ? ease.left : ease.right;
    let next: number | null = null;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') next = cur - STEP * big;
    else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') next = cur + STEP * big;
    else if (e.key === 'PageDown') next = cur - 0.1;
    else if (e.key === 'PageUp') next = cur + 0.1;
    else if (e.key === 'Home') next = which === 'left' ? 0 : ease.left + STEP;
    else if (e.key === 'End') next = which === 'left' ? ease.right - STEP : 1;
    if (next === null) return;
    e.preventDefault();
    e.stopPropagation();
    if (which === 'left') propose(next, ease.right, true);
    else propose(ease.left, next, true);
  };

  const thumb = (which: 'left' | 'right') => {
    const value = which === 'left' ? ease.left : ease.right;
    return (
      <div
        className={s.sliceThumb}
        style={{ left: `${String(value * 100)}%` }}
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label={t(which === 'left' ? m.sliceStart : m.sliceEnd)}
        aria-valuemin={0}
        aria-valuemax={1}
        aria-valuenow={value}
        aria-valuetext={value.toFixed(2)}
        aria-disabled={disabled || undefined}
        data-thumb={which}
        onKeyDown={thumbKeys(which)}
        onPointerDown={(e) => {
          if (disabled) return;
          e.preventDefault();
          e.stopPropagation();
          drag.current = which;
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (drag.current !== which) return;
          const x = fromPointer(e);
          if (which === 'left') propose(x, ease.right, false);
          else propose(ease.left, x, false);
        }}
        onPointerUp={(e) => {
          if (drag.current !== which) return;
          drag.current = null;
          e.currentTarget.releasePointerCapture(e.pointerId);
          props.onChange(ease, true);
        }}
      />
    );
  };

  const whole: EaseValue = { kind: 'tween', id: ease.id, left: 0, right: 1 };
  const isWhole = ease.left === 0 && ease.right === 1;
  return (
    <div className={s.slice} data-disabled={disabled || undefined}>
      <div className={s.sliceHeader}>
        <span className={s.sliceTitle}>{t(m.sliceTitle)}</span>
        <span className={s.sliceRange}>
          {isWhole ? t(m.sliceWhole) : t(m.sliceRange, { left: ease.left.toFixed(2), right: ease.right.toFixed(2) })}
        </span>
      </div>
      <div className={s.sliceRow}>
        <CurveThumb ease={whole} size={28} animate={false} showOvershoot />
        <div className={s.sliceBar} ref={barRef}>
          <div className={s.sliceTrack} />
          <div
            className={s.sliceRangeFill}
            style={{ left: `${String(ease.left * 100)}%`, width: `${String((ease.right - ease.left) * 100)}%` }}
          />
          {thumb('left')}
          {thumb('right')}
        </div>
        <CurveThumb ease={ease} size={28} animate={false} showOvershoot />
      </div>
      <div className={s.sliceFields}>
        <NumberField
          label={t(m.sliceStart)}
          value={{ kind: 'value', value: ease.left }}
          min={0}
          max={1}
          step={STEP}
          precision={2}
          disabled={disabled}
          onCommit={(v) => {
            if (v.kind === 'number') propose(v.value, ease.right, true);
          }}
        />
        <NumberField
          label={t(m.sliceEnd)}
          value={{ kind: 'value', value: ease.right }}
          min={0}
          max={1}
          step={STEP}
          precision={2}
          disabled={disabled}
          onCommit={(v) => {
            if (v.kind === 'number') propose(ease.left, v.value, true);
          }}
        />
        <Button
          size="sm"
          variant="ghost"
          disabledReason={disabled ? disabledReason : isWhole ? { key: m.sliceWhole } : null}
          onClick={() => {
            accept({ kind: 'tween', id: ease.id, left: 0, right: 1 }, true);
          }}
        >
          {t(m.sliceReset)}
        </Button>
      </div>
      {disabledReason !== null && <p className={s.note}>{tr(disabledReason)}</p>}
      {problem !== null && (
        <p className={s.error} role="alert">
          {tr(problem)}
        </p>
      )}
    </div>
  );
}
